import React, { useEffect, useRef, useState } from "react";
import { Button } from "reactstrap";
import { useAccountTranslation } from "../translations";

interface Props {
    initialValue?: string | null;
    onChange: (signature: string | null) => void;
}

const SignaturePad: React.FC<Props> = ({ initialValue = null, onChange }) => {
    const { t } = useAccountTranslation();
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const hasInkRef = useRef(false);
    const initialValueRef = useRef(initialValue);
    const [drawing, setDrawing] = useState(false);
    const [empty, setEmpty] = useState(true);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ratio = Math.max(window.devicePixelRatio || 1, 1);
        const width = canvas.clientWidth;
        canvas.width = width * ratio;
        canvas.height = 180 * ratio;
        const context = canvas.getContext("2d");
        if (!context) return;
        context.scale(ratio, ratio);
        context.fillStyle = "#ffffff";
        context.fillRect(0, 0, width, 180);
        context.lineWidth = 2.5;
        context.lineCap = "round";
        context.lineJoin = "round";
        context.strokeStyle = "#172b4d";

        if (initialValueRef.current) {
            const image = new Image();
            image.onload = () => {
                context.drawImage(image, 0, 0, width, 180);
                hasInkRef.current = true;
                setEmpty(false);
            };
            image.src = initialValueRef.current;
        }
    }, []);

    const position = (event: React.PointerEvent<HTMLCanvasElement>) => {
        const canvas = canvasRef.current!;
        const rect = canvas.getBoundingClientRect();
        return { x: event.clientX - rect.left, y: event.clientY - rect.top };
    };

    const start = (event: React.PointerEvent<HTMLCanvasElement>) => {
        const context = canvasRef.current?.getContext("2d");
        if (!context) return;
        const point = position(event);
        canvasRef.current?.setPointerCapture(event.pointerId);
        context.beginPath();
        context.moveTo(point.x, point.y);
        setDrawing(true);
    };

    const move = (event: React.PointerEvent<HTMLCanvasElement>) => {
        if (!drawing) return;
        const context = canvasRef.current?.getContext("2d");
        if (!context) return;
        const point = position(event);
        context.lineTo(point.x, point.y);
        context.stroke();
        hasInkRef.current = true;
        setEmpty(false);
    };

    const finish = () => {
        if (!drawing) return;
        setDrawing(false);
        onChange(hasInkRef.current ? canvasRef.current!.toDataURL("image/png") : null);
    };

    const clear = () => {
        const canvas = canvasRef.current;
        const context = canvas?.getContext("2d");
        if (canvas && context) {
            context.save();
            context.setTransform(1, 0, 0, 1, 0, 0);
            context.fillStyle = "#ffffff";
            context.fillRect(0, 0, canvas.width, canvas.height);
            context.restore();
        }
        hasInkRef.current = false;
        setEmpty(true);
        onChange(null);
    };

    return <>
        <div className="border rounded bg-white position-relative">
            {empty && <span className="position-absolute text-muted" style={{ left: 16, top: 14, pointerEvents: "none" }}>
                {t("signatureHint")}
            </span>}
            <canvas
                ref={canvasRef}
                aria-label={t("signatureFieldAria")}
                style={{ width: "100%", height: 180, display: "block", touchAction: "none", cursor: "crosshair" }}
                onPointerDown={start}
                onPointerMove={move}
                onPointerUp={finish}
                onPointerCancel={finish}
            />
        </div>
        <Button color="link" size="sm" className="px-0 mt-1" onClick={clear}>{t("redraw")}</Button>
    </>;
};

export default SignaturePad;
